/*
 * Lecture automatique « gated » des vidéos produit (cartes + galerie).
 *
 * Côté Liquid, `snippets/media.liquid` accepte `autoplay_gated: true` : la <video-media> reçoit
 * `data-autoplay-gated`, ses <source> sont rendues sans `src` (attribut `lazy-src`) et le <video>
 * est en `preload="none"`. Rien n'est téléchargé au chargement de la page, seul le poster s'affiche.
 *
 * Ce script attache les sources et lance la lecture (muet, inline) uniquement lorsque :
 *   - l'utilisateur a interagi une première fois (mousemove / touchstart / scroll / clavier / molette), ET
 *   - la vidéo est dans le champ de vision (IntersectionObserver, 25 % visible).
 * Sortie du champ de vision = pause. Mode `data-gated-mode="hover"` (2e média d'une carte) : la vidéo
 * joue tant que la carte est survolée à la souris, redémarre au début à chaque survol.
 *
 * Le nombre de vidéos « inview » jouant simultanément est plafonné (MAX_CONCURRENT) pour ménager
 * CPU et bande passante sur les listings. `prefers-reduced-motion` désactive l'autoplay inview.
 *
 * La lecture passe par l'API du custom element vendored (`video-media`.play()/pause()) pour que les
 * attributs `loaded` / `playing` et la logique `group` du thème restent cohérents.
 */
(() => {
  const SELECTOR = 'video-media[data-autoplay-gated]';
  const HOVER_SELECTOR = `${SELECTOR}[data-gated-mode="hover"]`;
  const MAX_CONCURRENT = 4;
  const INTERACTION_EVENTS = ['pointerdown', 'pointermove', 'touchstart', 'keydown', 'wheel', 'scroll'];
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  let interacted = false;
  const inView = new Set();
  const started = new Set();

  const isHover = (el) => el.getAttribute('data-gated-mode') === 'hover';

  /* Attache les sources et déclenche le chargement, une seule fois par élément. */
  const prepare = (el) => {
    if (el.hasAttribute('data-video-loaded')) return true;
    const video = el.querySelector('video');
    if (!video) return false;

    for (const source of video.querySelectorAll('source[lazy-src]')) {
      source.setAttribute('src', source.getAttribute('lazy-src'));
      source.removeAttribute('lazy-src');
    }

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.addEventListener('playing', () => el.setAttribute('data-video-ready', ''), { once: true });
    video.load();

    el.setAttribute('data-video-loaded', '');
    return true;
  };

  const play = (el, options) => {
    if (!prepare(el)) return;
    if (typeof el.play === 'function') el.play(options);
  };

  const pause = (el) => {
    if (typeof el.pause === 'function') el.pause();
  };

  /* Lance les vidéos visibles, dans la limite du plafond. */
  const schedule = () => {
    if (!interacted || reducedMotion.matches) return;
    for (const el of inView) {
      if (started.size >= MAX_CONCURRENT) break;
      if (started.has(el)) continue;
      started.add(el);
      play(el);
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const { target, isIntersecting } of entries) {
        if (isIntersecting) {
          inView.add(target);
        } else {
          inView.delete(target);
          if (started.delete(target)) pause(target);
        }
      }
      schedule();
    },
    { threshold: 0.25 },
  );

  const register = (el) => {
    if (el.hasAttribute('data-gated-registered')) return;
    el.setAttribute('data-gated-registered', '');
    if (!isHover(el)) observer.observe(el);
  };

  const scan = (root) => {
    if (root.matches?.(SELECTOR)) register(root);
    root.querySelectorAll?.(SELECTOR).forEach(register);
  };

  /* Première interaction : débloque l'autoplay inview. */
  const onInteract = () => {
    if (interacted) return;
    interacted = true;
    INTERACTION_EVENTS.forEach((type) => window.removeEventListener(type, onInteract, true));
    schedule();
  };

  /* Survol des cartes (souris uniquement). */
  let hoveredCard = null;

  const hoverVideo = (card) => card?.querySelector(HOVER_SELECTOR);

  const leaveCard = () => {
    const el = hoverVideo(hoveredCard);
    if (el) pause(el);
    hoveredCard = null;
  };

  const onPointerOver = (event) => {
    if (event.pointerType !== 'mouse') return;
    const card = event.target.closest?.('product-card') ?? null;
    if (card === hoveredCard) return;
    if (hoveredCard) leaveCard();
    if (!card) return;
    const el = hoverVideo(card);
    if (!el) return;
    hoveredCard = card;
    play(el, { restart: true });
  };

  const onPointerOut = (event) => {
    if (!hoveredCard) return;
    if (event.relatedTarget && hoveredCard.contains(event.relatedTarget)) return;
    leaveCard();
  };

  const init = () => {
    scan(document.body);

    new MutationObserver((mutations) => {
      for (const { addedNodes } of mutations) {
        addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) scan(node);
        });
      }
    }).observe(document.body, { childList: true, subtree: true });

    INTERACTION_EVENTS.forEach((type) =>
      window.addEventListener(type, onInteract, { capture: true, passive: true }),
    );

    document.addEventListener('pointerover', onPointerOver, { passive: true });
    document.addEventListener('pointerout', onPointerOut, { passive: true });
  };

  const ready = () => customElements.whenDefined('video-media').then(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready, { once: true });
  } else {
    ready();
  }
})();
